import { decode, encodeConnect, pong } from 'ProdantixSdk/chat/socket/frames';
import { TransportError } from 'ProdantixSdk/core/errors';
import type { SocketFactory, SocketLike } from 'ProdantixSdk/core/types';

export interface FlagStreamOptions {
  /// The `prodantix-ws` origin, told to us by the snapshot rather than derived
  /// from the edge host: the two are different services on different hostnames.
  host: string;
  projectKey: string;
  /// Called once per burst of `flagChange` frames with the last key seen; the
  /// caller refetches the snapshot, which carries every flag anyway.
  onChange: (key: string) => void;
  /// Called whenever the connection is not carrying frames. `retryable` is
  /// false when the server refused the handshake, which no retry improves.
  onClosed: (retryable: boolean) => void;
  socketFactory?: SocketFactory;
  /// Injected so a test does not wait real milliseconds.
  setTimer?: (run: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
}

/// Direct websocket, never the polling transport, for the reasons the chat
/// socket records: no upgrade dance and no CORS preflight.
const PATH = '/socket.io/?EIO=4&transport=websocket';

/// How long a burst of change frames is held before one callback fires. A
/// rollout step or a multi-flag save lands as several frames in a row, and
/// each callback is a snapshot fetch.
const COALESCE_MS = 250;

/// A server SDK's subscription to a project's flag changes. It only ever
/// listens: the SDK reads flags over REST, so nothing here emits an event.
export class FlagStream {
  private socket: SocketLike | null = null;
  private heartbeat: unknown = null;
  private pending: unknown = null;
  private lastKey = '';
  private ready = false;
  private closed = false;
  private heartbeatWindow = 45_000;
  private readonly factory: SocketFactory;
  private readonly setTimer: (run: () => void, ms: number) => unknown;
  private readonly clearTimer: (handle: unknown) => void;

  constructor(private readonly options: FlagStreamOptions) {
    this.factory = options.socketFactory ?? defaultFactory;
    this.setTimer = options.setTimer ?? ((run, ms) => setTimeout(run, ms));
    this.clearTimer = options.clearTimer ?? (handle => clearTimeout(handle as ReturnType<typeof setTimeout>));
  }

  open(): void {
    if (this.socket || this.closed) return;
    const url = `${this.options.host.replace(/^http/, 'ws').replace(/\/+$/, '')}${PATH}`;
    let socket: SocketLike;
    try {
      socket = this.factory(url);
    } catch {
      this.options.onClosed(false);
      return;
    }
    this.socket = socket;
    socket.onmessage = event => this.receive(String(event.data));
    socket.onerror = () => socket.close();
    socket.onclose = () => this.fell();
  }

  close(): void {
    this.closed = true;
    this.stopHeartbeat();
    if (this.pending !== null) this.clearTimer(this.pending);
    this.pending = null;
    const socket = this.socket;
    this.socket = null;
    socket?.close();
  }

  private receive(raw: string): void {
    const frame = decode(raw);
    if (frame.kind === 'open') {
      this.armHeartbeat(frame.pingInterval + frame.pingTimeout);
      this.socket?.send(encodeConnect({ projectKey: this.options.projectKey }));
      return;
    }
    if (frame.kind === 'ping') {
      this.armHeartbeat();
      this.socket?.send(pong());
      return;
    }
    if (frame.kind === 'connectError' || frame.kind === 'disconnect') {
      this.stopHeartbeat();
      this.socket?.close();
      return;
    }
    if (frame.kind !== 'event') return;
    if (frame.name === 'ready') {
      this.ready = true;
      return;
    }
    if (frame.name === 'flagChange') {
      const key = (frame.payload as { key?: unknown } | undefined)?.key;
      this.lastKey = typeof key === 'string' ? key : '';
      if (this.pending === null) {
        this.pending = this.setTimer(() => {
          this.pending = null;
          this.options.onChange(this.lastKey);
        }, COALESCE_MS);
      }
    }
  }

  /// A close before `ready` means the handshake was refused: the key is not a
  /// project key of this region, and retrying cannot change that.
  private fell(): void {
    this.stopHeartbeat();
    const retryable = this.ready;
    this.socket = null;
    this.ready = false;
    if (!this.closed) this.options.onClosed(retryable);
  }

  private armHeartbeat(ms?: number): void {
    if (ms !== undefined) this.heartbeatWindow = ms;
    this.stopHeartbeat();
    this.heartbeat = this.setTimer(() => this.socket?.close(), this.heartbeatWindow);
  }

  private stopHeartbeat(): void {
    if (this.heartbeat !== null) this.clearTimer(this.heartbeat);
    this.heartbeat = null;
  }
}

function defaultFactory(url: string): SocketLike {
  const ctor = (globalThis as { WebSocket?: new (url: string) => SocketLike }).WebSocket;
  if (!ctor) throw new TransportError('no WebSocket');
  return new ctor(url);
}
